import React, { useState, useRef } from "react";
import * as XLSX from "xlsx";
import { showSuccess, showError } from "../utils/showToast";
import { isConverseUser } from "../utils/companyConfig";
import { API_BASE_URL } from "../config/api"; 

interface BulkUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    memberUuid?: string;
}

interface CsvRow {
    [key: string]: string;
}

interface ParsedCsv {
    headers: string[];
    rows: CsvRow[];
    rawRows: string[][];
}

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    memberUuid: memberUuidProp,
}) => {
    const [file, setFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState<any>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [parsedData, setParsedData] = useState<ParsedCsv | null>(null);
    const [parseError, setParseError] = useState("");
    const [showPreview, setShowPreview] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const memberUuid = memberUuidProp || localStorage.getItem("memberUuid");
    const isConverse = isConverseUser(memberUuid);

    // ── sanitize comma-formatted numbers before upload ──
    const sanitizeNumericFields = (rows: CsvRow[], headers: string[]): CsvRow[] => {
        const numericFieldPatterns = [
            /product\s*price/i,
            /care\s*price/i,
            /price/i,
        ];

        return rows.map((row) => {
            const sanitized: CsvRow = { ...row };
            headers.forEach((header) => {
                const isNumericField = numericFieldPatterns.some((pattern) =>
                    pattern.test(header)
                );
                if (isNumericField && sanitized[header]) {
                    sanitized[header] = sanitized[header].replace(/,/g, "").trim();
                }
            });
            return sanitized;
        });
    };

    // Parse CSV content
    const parseCSV = (content: string): ParsedCsv => {
        const lines = content
            .replace(/\r\n/g, "\n")
            .replace(/\r/g, "\n")
            .split("\n")
            .map((line) => line.trim())
            .filter((line) => line.length > 0);

        if (lines.length === 0) throw new Error("CSV file is empty");

        const headers = parseCSVLine(lines[0]);
        if (headers.length === 0) throw new Error("CSV file has no headers");

        const rows: CsvRow[] = [];

        for (let i = 1; i < lines.length; i++) {
            const values = parseCSVLine(lines[i]);
            if (values.every((v) => v === "")) continue;

            const row: CsvRow = {};
            headers.forEach((header, index) => {
                row[header] = values[index] || "";
            });
            rows.push(row);
        }

        const sanitizedRows = sanitizeNumericFields(rows, headers);
        const sanitizedRawRows = sanitizedRows.map((r) => headers.map((h) => r[h]));

        return { headers, rows: sanitizedRows, rawRows: sanitizedRawRows };
    };

    const parseCSVLine = (line: string): string[] => {
        const result: string[] = [];
        let current = "";
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            const nextChar = line[i + 1];

            if (char === '"') {
                if (inQuotes && nextChar === '"') {
                    current += '"';
                    i++;
                } else {
                    inQuotes = !inQuotes;
                }
            } else if (char === "," && !inQuotes) {
                result.push(current.trim());
                current = "";
            } else {
                current += char;
            }
        }

        result.push(current.trim());
        return result;
    };

    // Parse XLSX/XLS file into ParsedCsv shape
    const parseXLSX = (file: File): Promise<ParsedCsv> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = e.target?.result;
                    const workbook = XLSX.read(data, { type: "array", cellDates: false });

                    const firstSheetName = workbook.SheetNames[0];
                    if (!firstSheetName) {
                        reject(new Error("XLSX file contains no sheets"));
                        return;
                    }

                    const worksheet = workbook.Sheets[firstSheetName];
                    const jsonRows: any[] = XLSX.utils.sheet_to_json(worksheet, {
                        defval: "",
                    });

                    if (jsonRows.length === 0) {
                        reject(new Error("XLSX file contains no data rows"));
                        return;
                    }

                    const headers = Object.keys(jsonRows[0]);
                    const rows: CsvRow[] = jsonRows.map((r) => {
                        const row: CsvRow = {};
                        headers.forEach((h) => {
                            row[h] = String(r[h] ?? "");
                        });
                        return row;
                    });

                    const sanitizedRows = sanitizeNumericFields(rows, headers);
                    const rawRows: string[][] = sanitizedRows.map((r) =>
                        headers.map((h) => r[h])
                    );

                    resolve({ headers, rows: sanitizedRows, rawRows });
                } catch (err: any) {
                    reject(new Error(err.message || "Failed to parse XLSX file"));
                }
            };
            reader.onerror = () => reject(new Error("Failed to read file"));
            reader.readAsArrayBuffer(file);
        });
    };

    const readFileContent = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target?.result as string);
            reader.onerror = () => reject(new Error("Failed to read file"));
            reader.readAsText(file);
        });
    };

    // Handle file selection
    const handleFileSelect = async (selectedFile: File) => {
        setError("");
        setParseError("");
        setResult(null);
        setParsedData(null);
        setShowPreview(false);

        const name = selectedFile.name.toLowerCase();
        const isCSV = name.endsWith(".csv");
        const isXLSX = name.endsWith(".xlsx") || name.endsWith(".xls");

        if (isConverse) {
            if (!isCSV && !isXLSX) {
                setError("Please upload a CSV (.csv) or Excel (.xlsx) file");
                return;
            }
        } else {
            if (!isCSV) {
                setError("Please upload a CSV file");
                return;
            }
        }

        if (selectedFile.size > 10 * 1024 * 1024) {
            setError("File size exceeds 10MB limit");
            return;
        }

        setFile(selectedFile);

        try {
            let parsed: ParsedCsv;

            if (isXLSX) {
                parsed = await parseXLSX(selectedFile);
            } else {
                const content = await readFileContent(selectedFile);
                parsed = parseCSV(content);
            }

            if (parsed.rows.length === 0) {
                setParseError("File contains no data rows");
                return;
            }

            setParsedData(parsed);
            setShowPreview(true);
        } catch (err: any) {
            setParseError(err.message || "Failed to parse file");
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) handleFileSelect(droppedFile);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) handleFileSelect(selectedFile);
    };

    const handleUpload = async () => {
        if (!file || !parsedData) {
            setError("Please select a file");
            return;
        }

        const storedMemberUuid = memberUuid;
        const userEmail = localStorage.getItem("userEmail") || "user@example.com";

        if (!storedMemberUuid) {
            setError("Member UUID not found. Please log in again.");
            return;
        }

        setLoading(true);
        setError("");

        const formData = new FormData();

        const fileName = file.name.toLowerCase();
        const isXLSX = fileName.endsWith(".xlsx") || fileName.endsWith(".xls");

        if (isXLSX) {
            formData.append("csvFile", file);
        } else {
            const sanitizedCsvContent = [
                parsedData.headers.join(","),
                ...parsedData.rows.map((row) =>
                    parsedData.headers
                        .map((h) => {
                            const val = row[h] ?? "";
                            if (
                                val.includes(",") ||
                                val.includes('"') ||
                                val.includes("\n")
                            ) {
                                return `"${val.replace(/"/g, '""')}"`;
                            }
                            return val;
                        })
                        .join(",")
                ),
            ].join("\n");

            const sanitizedBlob = new Blob([sanitizedCsvContent], {
                type: "text/csv",
            });
            const sanitizedFile = new File([sanitizedBlob], file.name, {
                type: "text/csv",
            });
            formData.append("csvFile", sanitizedFile);
        }

        formData.append("memberUuid", storedMemberUuid);
        formData.append("uploadedBy", userEmail);

        try {
            const response = await fetch(
                `${API_BASE_URL}/api/sale/bulk-upload`,
                {
                    method: "POST",
                    headers: {
                        "x-member-uuid": storedMemberUuid,
                        "x-user-email": userEmail,
                    },
                    body: formData,
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || data.message || "Upload failed");
            }

            setResult(data);
            setShowPreview(false);

            if (data.failed === 0) {
                const skipped = data.skipped ?? 0;
                const subtitle =
                    skipped > 0
                        ? `${data.successful} records uploaded. ${skipped} skipped (already exist).`
                        : `All ${data.successful} records uploaded successfully.`;
                showSuccess("Upload Complete", subtitle);
                handleClose();
                onSuccess();
            }
        } catch (err: any) {
            showError("Upload Failed", err.message || "Failed to upload file");
            setError(err.message || "Failed to upload file");
        } finally {
            setLoading(false);
        }
    };

    const downloadTemplate = () => {
        window.open(
            `${API_BASE_URL}/api/sale/csv-template`,
            "_blank"
        );
    };

    const resetForm = () => {
        setFile(null);
        setParsedData(null);
        setShowPreview(false);
        setError("");
        setParseError("");
        setResult(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const getHeaderDisplayName = (header: string): string => {
        return header.replace(/\s*\(optional\)\s*$/i, "").trim();
    };

    const isFieldPossiblyInvalid = (header: string, value: string): boolean => {
        const cleanHeader = header
            .toLowerCase()
            .replace(/\s*\(optional\)\s*$/i, "")
            .trim();

        if (
            (cleanHeader.includes("phone") || cleanHeader.includes("mobile")) &&
            value
        ) {
            const cleanPhone = value
                .replace(/\s+/g, "")
                .replace(/^\+91/, "")
                .replace(/^91/, "");
            if (!/^[6-9]\d{9}$/.test(cleanPhone)) return true;
        }

        if (
            (cleanHeader === "care price" || cleanHeader === "product price") &&
            value
        ) {
            const cleanValue = value.replace(/,/g, "").trim();
            if (isNaN(parseFloat(cleanValue)) || parseFloat(cleanValue) <= 0)
                return true;
        }

        const requiredFields = [
            "order id",
            "product name",
            "phone number",
            "care price",
        ];
        if (
            requiredFields.some((f) => cleanHeader === f) &&
            !value.trim()
        ) {
            return true;
        }

        if (cleanHeader === "email" && value && value.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value.trim())) return true;
        }

        // ✅ FIX: Allow DD/MM/YYYY, DD-MM-YYYY, YYYY-MM-DD, YYYY/MM/DD and optional time
        if (cleanHeader === "transaction date" && value && value.trim()) {
            const datePart = value.trim().split(/\s+/)[0];
            const dmyMatch = datePart.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
            const isoMatch = datePart.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/);
            if (!dmyMatch && !isoMatch) return true;
        }

        if (cleanHeader === "care+ sold" && value && value.trim()) {
            const validSoldValues = ["yes", "no", "true", "false", "1", "0"];
            if (!validSoldValues.includes(value.trim().toLowerCase())) return true;
        }

        if (cleanHeader === "gender" && value && value.trim()) {
            const validGenderValues = [
                "male",
                "female",
                "unisex",
                "m",
                "f",
                "u",
                "-",
                "",
            ];
            if (!validGenderValues.includes(value.trim().toLowerCase())) return true;
        }

        if (cleanHeader === "size type" && value && value.trim()) {
            const validSizeTypes = ["eu", "us", "uk", "in", "cm", "-", ""];
            if (!validSizeTypes.includes(value.trim().toLowerCase())) return true;
        }

        return false;
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={handleClose}
        >
            <div
                className={`bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 sm:p-8 shadow-2xl border border-gray-700 max-h-[90vh] overflow-hidden flex flex-col transition-all duration-300 ${
                    showPreview ? "w-full max-w-6xl" : "w-full max-w-2xl"
                }`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex justify-between items-center mb-6 flex-shrink-0">
                    <div>
                        <h2 className="text-2xl font-bold">Bulk Upload Care+ Orders</h2>
                        {showPreview && parsedData && (
                            <p className="text-sm text-gray-400 mt-1">
                                {parsedData.rows.length} records found in {file?.name}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-white text-2xl transition p-2"
                    >
                        ✕
                    </button>
                </div>

                {/* Content - Scrollable */}
                <div className="flex-1 overflow-y-auto min-h-0">
                    {!showPreview ? (
                        <>
                            {/* Upload Result */}
                            {result && (
                                <div
                                    className={`mb-6 rounded-xl border overflow-hidden ${
                                        result.failed === 0
                                            ? "border-green-500/40"
                                            : result.successful > 0
                                              ? "border-yellow-500/40"
                                              : "border-red-500/40"
                                    }`}
                                >
                                    {/* Banner header */}
                                    <div
                                        className={`px-4 py-3 flex items-center gap-3 ${
                                            result.failed === 0
                                                ? "bg-green-500/15"
                                                : result.successful > 0
                                                  ? "bg-yellow-500/10"
                                                  : "bg-red-500/10"
                                        }`}
                                    >
                                        <div
                                            className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                                                result.failed === 0
                                                    ? "bg-green-500/25"
                                                    : result.successful > 0
                                                      ? "bg-yellow-500/25"
                                                      : "bg-red-500/25"
                                            }`}
                                        >
                                            {result.failed === 0 ? (
                                                <svg
                                                    className="w-5 h-5 text-green-400"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2.5}
                                                        d="M5 13l4 4L19 7"
                                                    />
                                                </svg>
                                            ) : (
                                                <svg
                                                    className="w-5 h-5 text-red-400"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                                                    />
                                                </svg>
                                            )}
                                        </div>
                                        <div>
                                            <p
                                                className={`font-semibold text-sm ${
                                                    result.failed === 0
                                                        ? "text-green-300"
                                                        : result.successful > 0
                                                          ? "text-yellow-300"
                                                          : "text-red-300"
                                                }`}
                                            >
                                                {result.failed === 0
                                                    ? "Upload Successful! 🎉"
                                                    : result.successful > 0
                                                      ? "Partially Uploaded — some rows had issues"
                                                      : "Upload Failed — all rows had errors"}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-0.5">
                                                {result.failed === 0
                                                    ? "All records were processed successfully."
                                                    : "Fix the errors below and try uploading again."}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Stats row */}
                                    <div className="grid grid-cols-3 gap-px bg-gray-700/30">
                                        <div className="bg-gray-900/60 px-4 py-3 text-center">
                                            <p className="text-2xl font-bold text-white">
                                                {result.totalRecords}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-0.5">
                                                Total Rows
                                            </p>
                                        </div>
                                        <div className="bg-gray-900/60 px-4 py-3 text-center">
                                            <p className="text-2xl font-bold text-green-400">
                                                {result.successful}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-0.5">
                                                Uploaded
                                            </p>
                                        </div>
                                        <div className="bg-gray-900/60 px-4 py-3 text-center">
                                            <p className="text-2xl font-bold text-red-400">
                                                {result.failed}
                                            </p>
                                            <p className="text-xs text-gray-400 mt-0.5">Failed</p>
                                        </div>
                                    </div>

                                    {/* Skipped notice */}
                                    {result.skipped > 0 && (
                                        <div className="px-4 py-2 bg-yellow-500/5 border-t border-yellow-500/20 flex items-center gap-2 text-yellow-400 text-sm">
                                            <svg
                                                className="w-4 h-4 flex-shrink-0"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            </svg>
                                            <span>
                                                <span className="font-semibold">
                                                    {result.skipped}
                                                </span>{" "}
                                                rows skipped — already exist in the database
                                            </span>
                                        </div>
                                    )}

                                    {/* Error list */}
                                    {result.failed > 0 &&
                                        result.errors &&
                                        result.errors.length > 0 && (
                                            <div className="border-t border-gray-700/50">
                                                <div className="px-4 py-2 bg-red-500/5 flex items-center gap-2">
                                                    <svg
                                                        className="w-4 h-4 text-red-400 flex-shrink-0"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                                        />
                                                    </svg>
                                                    <p className="text-xs font-semibold text-red-300 uppercase tracking-wider">
                                                        Error Details
                                                    </p>
                                                </div>
                                                <div className="max-h-36 overflow-y-auto px-4 pb-3 space-y-2 pt-2">
                                                    {result.errors
                                                        .slice(0, 10)
                                                        .map((err: any, idx: number) => (
                                                            <div
                                                                key={idx}
                                                                className="flex items-start gap-2 text-xs"
                                                            >
                                                                <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 font-bold text-[10px]">
                                                                    !
                                                                </span>
                                                                <span className="text-gray-300">
                                                                    <span className="text-red-300 font-medium">
                                                                        Row {err.row}:
                                                                    </span>{" "}
                                                                    {err.message}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    {result.errors.length > 10 && (
                                                        <p className="text-xs text-gray-500 pl-7">
                                                            +{result.errors.length - 10} more errors
                                                            not shown
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                </div>
                            )}

                            {/* Download Template Button — hidden for Converse */}
                            {!isConverse && (
                                <div className="mb-6">
                                    <button
                                        onClick={downloadTemplate}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition"
                                    >
                                        <svg
                                            className="w-5 h-5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                            />
                                        </svg>
                                        Download Sample CSV Template
                                    </button>
                                    <p className="text-xs text-gray-400 mt-2 text-center">
                                        Download the template, fill in your data, and upload below
                                    </p>
                                </div>
                            )}

                            {/* Error Messages */}
                            {(error || parseError) && (
                                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-start gap-2">
                                    <svg
                                        className="w-5 h-5 flex-shrink-0 mt-0.5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                    <span>{error || parseError}</span>
                                </div>
                            )}

                            {/* Drag and Drop Area */}
                            <div
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                className={`border-2 border-dashed rounded-xl p-8 text-center transition cursor-pointer ${
                                    isDragging
                                        ? "border-cyan-500 bg-cyan-500/10"
                                        : "border-gray-600 hover:border-gray-500"
                                }`}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept={isConverse ? ".csv,.xlsx,.xls" : ".csv"}
                                    onChange={handleFileChange}
                                    className="hidden"
                                />
                                <div className="space-y-4">
                                    <svg
                                        className="w-16 h-16 mx-auto text-gray-500"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                        />
                                    </svg>
                                    <div>
                                        <p className="text-gray-300 mb-2">
                                            {isConverse
                                                ? "Drag and Drop Your SAP Report"
                                                : "Drag and drop your CSV file here"}
                                        </p>
                                        <p className="text-gray-500 text-sm mb-4">or</p>
                                        <span className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition inline-block">
                                            Browse Files
                                        </span>
                                    </div>
                                    <p className="text-xs text-gray-500">
                                        {isConverse
                                            ? "Accepted: .csv, .xlsx (Max 10MB)"
                                            : "Only .csv files are accepted (Max 10MB)"}
                                    </p>
                                </div>
                            </div>
                        </>
                    ) : (
                        /* Preview */
                        <>
                            {/* File Info Bar */}
                            <div className="flex items-center justify-between gap-4 p-3 bg-gray-800/50 rounded-lg mb-4 flex-shrink-0">
                                <div className="flex items-center gap-3">
                                    <svg
                                        className="w-8 h-8 text-green-400"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                        />
                                    </svg>
                                    <div>
                                        <p className="text-white font-medium">{file?.name}</p>
                                        <p className="text-gray-400 text-xs">
                                            {((file?.size ?? 0) / 1024).toFixed(2)} KB •{" "}
                                            {parsedData?.rows.length} rows •{" "}
                                            {parsedData?.headers.length} columns
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={resetForm}
                                    className="text-red-400 hover:text-red-300 text-sm underline flex items-center gap-1"
                                >
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                        />
                                    </svg>
                                    Remove
                                </button>
                            </div>

                            {/* Converse info notice */}
                            {isConverse && (
                                <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-blue-300 text-sm flex items-start gap-3">
                                    <svg
                                        className="w-5 h-5 flex-shrink-0 mt-0.5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                    <span>
                                        Only invoices containing CarePlus with Footwear - Converse
                                        line items will be imported. The preview shows all raw rows
                                        from the file.
                                    </span>
                                </div>
                            )}

                            {/* Error Message */}
                            {error && (
                                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-start gap-2">
                                    <svg
                                        className="w-5 h-5 flex-shrink-0 mt-0.5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                    <span>{error}</span>
                                </div>
                            )}

                            {/* Preview Header */}
                            <div className="flex items-center justify-between mb-3 flex-shrink-0">
                                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                                    <svg
                                        className="w-5 h-5 text-cyan-400"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                        />
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                        />
                                    </svg>
                                    Data Preview
                                </h3>
                                <span className="text-xs text-gray-400 bg-gray-700 px-2 py-1 rounded">
                                    Showing {Math.min(parsedData?.rows.length || 0, 100)} of{" "}
                                    {parsedData?.rows.length} rows
                                </span>
                            </div>

                            {/* Preview Table */}
                            <div className="border border-gray-700 rounded-lg overflow-hidden">
                                <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                                    <table className="w-full text-sm">
                                        <thead className="bg-gray-800 sticky top-0 z-10">
                                            <tr>
                                                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider border-b border-gray-700 bg-gray-800">
                                                    #
                                                </th>
                                                {parsedData?.headers.map((header, idx) => (
                                                    <th
                                                        key={idx}
                                                        className="px-3 py-2 text-left text-xs font-semibold text-cyan-400 uppercase tracking-wider border-b border-gray-700 whitespace-nowrap bg-gray-800"
                                                    >
                                                        {getHeaderDisplayName(header)}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-700/50">
                                            {parsedData?.rows.slice(0, 100).map((row, rowIdx) => (
                                                <tr
                                                    key={rowIdx}
                                                    className={`${
                                                        rowIdx % 2 === 0
                                                            ? "bg-gray-900/30"
                                                            : "bg-gray-900/10"
                                                    } hover:bg-gray-700/30 transition-colors`}
                                                >
                                                    <td className="px-3 py-2 text-gray-500 text-xs font-mono">
                                                        {rowIdx + 2}
                                                    </td>
                                                    {parsedData?.headers.map((header, colIdx) => {
                                                        const value = row[header] || "";
                                                        const isInvalid =
                                                            !isConverse &&
                                                            isFieldPossiblyInvalid(header, value);

                                                        return (
                                                            <td
                                                                key={colIdx}
                                                                className={`px-3 py-2 whitespace-nowrap max-w-[200px] truncate ${
                                                                    isInvalid
                                                                        ? "text-red-400 bg-red-500/10"
                                                                        : "text-gray-300"
                                                                }`}
                                                                title={value}
                                                            >
                                                                {value || (
                                                                    <span className="text-gray-600 italic">
                                                                        empty
                                                                    </span>
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Preview Legend — standard only */}
                            {!isConverse && (
                                <div className="mt-3 flex flex-wrap gap-4 text-xs text-gray-400">
                                    <span className="flex items-center gap-1">
                                        <span className="w-3 h-3 bg-red-500/30 rounded"></span>
                                        Potentially invalid value
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <span className="text-gray-600 italic">empty</span>= Empty/missing value
                                    </span>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer Buttons */}
                <div className="flex gap-3 mt-6 pt-4 border-t border-gray-700 flex-shrink-0">
                    {showPreview ? (
                        <>
                            <button
                                onClick={resetForm}
                                className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition flex items-center justify-center gap-2"
                            >
                                <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M15 19l-7-7 7-7"
                                    />
                                </svg>
                                Back
                            </button>
                            <button
                                onClick={handleUpload}
                                disabled={loading}
                                className="flex-[2] px-4 py-2.5 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {loading ? (
                                    <>
                                        <svg
                                            className="animate-spin h-5 w-5"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                        >
                                            <circle
                                                className="opacity-25"
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                            />
                                            <path
                                                className="opacity-75"
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                            />
                                        </svg>
                                        Uploading {parsedData?.rows.length} records...
                                    </>
                                ) : (
                                    <>
                                        <svg
                                            className="w-5 h-5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                            />
                                        </svg>
                                        {isConverse
                                            ? `Upload SAP Report (${parsedData?.rows.length} rows)`
                                            : `Upload ${parsedData?.rows.length} Records`}
                                    </>
                                )}
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={handleClose}
                                className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition"
                            >
                                Cancel
                            </button>
                            <button
                                disabled
                                className="flex-1 px-4 py-2.5 bg-gray-600 text-gray-400 rounded-lg font-medium cursor-not-allowed"
                            >
                                Select a file to preview
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BulkUploadModal;